"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useEntryExitAnimation = useEntryExitAnimation;
var _react = require("react");
var _motionFunctions = require("../motion-functions");
/**
 * Custom hook for managing entry/exit (fade) animations with motion settings
 *
 * @param {boolean} shouldShow - Whether the element should be visible
 * @param {number} duration - Animation duration in milliseconds (default: 200)
 * @param {object} [options] - Motion options
 * @param {('swift'|'fluid'|'rebound')} [options.mode='fluid'] - Easing mode
 * @param {string|string[]} [options.properties='opacity'] - CSS properties to animate
 * @returns {object} Animation state and handlers
 */
function useEntryExitAnimation(shouldShow, duration = 200, options = {}) {
  const [isMounted, setIsMounted] = (0, _react.useState)(shouldShow);
  const [isVisible, setIsVisible] = (0, _react.useState)(shouldShow);
  const timeoutRef = (0, _react.useRef)(null);
  const raf1Ref = (0, _react.useRef)(0);
  const raf2Ref = (0, _react.useRef)(0);
  const {
    mode = 'fluid',
    properties = 'opacity'
  } = options || {};
  const transitionString = (0, _react.useMemo)(() => {
    const ease = (0, _motionFunctions.getEase)(mode);
    const easeStr = `cubic-bezier(${ease.join(', ')})`;
    return Array.isArray(properties) ? properties.map(p => `${p} ${duration}ms ${easeStr}`).join(', ') : `${properties} ${duration}ms ${easeStr}`;
  }, [mode, properties, duration]);
  (0, _react.useEffect)(() => {
    if (shouldShow) {
      // Mount first, then mark visible on next paint to trigger CSS transition
      setIsMounted(true);
      raf1Ref.current = requestAnimationFrame(() => {
        raf2Ref.current = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      // Start fade-out, then unmount after the transition
      setIsVisible(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (duration <= 0) {
        setIsMounted(false);
      } else {
        timeoutRef.current = setTimeout(() => {
          setIsMounted(false);
        }, duration);
      }
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (raf1Ref.current) {
        cancelAnimationFrame(raf1Ref.current);
      }
      if (raf2Ref.current) {
        cancelAnimationFrame(raf2Ref.current);
      }
    };
  }, [shouldShow, duration]);
  const fadeClasses = {
    base: 'fade-element',
    visible: isVisible ? 'fade-visible' : 'fade-hidden'
  };
  const fadeStyle = {
    opacity: isVisible ? 1 : 0,
    transition: transitionString,
    pointerEvents: isVisible ? 'auto' : 'none'
  };
  return {
    isMounted,
    isVisible,
    fadeClasses,
    fadeStyle
  };
}